import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';

/**
 * Trailing-debounce window (ms) for the close-hub aggregate refresh. Long enough to
 * coalesce a treasurer rapidly checking off rows, short enough that the fee nudge /
 * readiness pips feel live the moment they pause.
 */
const CLOSE_HUB_REFRESH_MS = 300;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Coalesce the EXPENSIVE close-hub aggregate refetches across a burst of ledger status
 * flips (confirm / void / mark-reported). Each flip hook keeps its cheap per-row/list
 * invalidations immediate (instant optimistic feedback + the triage count), but these three
 * are server-side recomputes — the filing preflight (fee nudge + hard blockers),
 * finance-health (readiness pips), and the close-hub period — so a rapid sequence of flips
 * schedules a SINGLE trailing refresh instead of one set per click.
 *
 * The timer is module-level on purpose: it coalesces across ALL flip hooks (contribution +
 * expenditure) and every row, since they all feed the same three aggregates. `invalidateQueries`
 * defaults to `refetchType: 'active'`, so the eventual refetch still only hits the network for
 * queries mounted on the close page (health also feeds the dashboard). See apps/web/CLAUDE.md.
 */
export function scheduleCloseHubRefresh(queryClient: QueryClient): void {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.close.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.preflightPrefix() });
    queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
  }, CLOSE_HUB_REFRESH_MS);
}
